import { z } from "zod";

const normalizedEmail = z
  .string()
  .trim()
  .pipe(z.email("Enter a valid email address."))
  .transform((value) => value.toLowerCase());

export const passwordSchema = z
  .string()
  .min(10, "Use at least 10 characters.")
  .max(128, "Keep the password under 128 characters.")
  .regex(/[a-z]/, "Add a lowercase letter.")
  .regex(/[A-Z]/, "Add an uppercase letter.")
  .regex(/[0-9]/, "Add a number.");

export const signupEmailSchema = z.object({
  email: normalizedEmail,
});

export const signupSchema = z.object({
  email: normalizedEmail,
  businessName: z
    .string()
    .trim()
    .min(2, "Enter your business name.")
    .max(80, "Keep the business name under 80 characters."),
  instagramHandle: z
    .string()
    .trim()
    .min(2, "Enter your Instagram handle.")
    .max(31, "Instagram handles are at most 30 characters.")
    .transform((value) => value.replace(/^@/, "").toLowerCase())
    .pipe(
      z
        .string()
        .regex(
          /^[a-z0-9._]+$/,
          "Use only letters, numbers, periods, and underscores.",
        ),
    ),
  password: passwordSchema,
});

export const loginSchema = z.object({
  email: normalizedEmail,
  password: z.string().min(1, "Enter your password.").max(128),
  keepSignedIn: z.boolean().default(false),
});

export const publicUserSchema = z.object({
  id: z.string().uuid(),
  email: z.email(),
  emailVerified: z.boolean(),
  createdAt: z.string(),
});

export const publicWorkspaceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  instagramHandle: z.string(),
  role: z.literal("owner"),
  createdAt: z.string(),
});

export const authSuccessSchema = z.object({
  user: publicUserSchema,
  workspace: publicWorkspaceSchema,
});

export const verificationRequiredSchema = z.object({
  requiresEmailVerification: z.literal(true),
  email: z.email(),
  message: z.string(),
});

export const authResultSchema = z.union([
  authSuccessSchema,
  verificationRequiredSchema,
]);

export const forgotPasswordSchema = z.object({
  email: normalizedEmail,
});

export const resetPasswordSchema = z
  .object({
    token: z.string().trim().min(1, "The reset token is missing."),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export const resetPasswordRequestSchema = z.object({
  token: z.string().trim().min(1, "The reset token is missing."),
  password: passwordSchema,
});

export const verifyEmailSchema = z.object({
  token: z.string().trim().min(1, "The verification token is missing."),
});

export const resendVerificationSchema = z.object({
  email: normalizedEmail,
});

export const messageResponseSchema = z.object({
  ok: z.literal(true),
  message: z.string(),
  debugUrl: z.url().optional(),
});

export type SignupEmailInput = z.input<typeof signupEmailSchema>;
export type SignupInput = z.input<typeof signupSchema>;
export type LoginInput = z.input<typeof loginSchema>;
export type ForgotPasswordInput = z.input<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.input<typeof resetPasswordRequestSchema>;
export type VerifyEmailInput = z.input<typeof verifyEmailSchema>;
export type ResendVerificationInput = z.input<
  typeof resendVerificationSchema
>;
export type AuthSuccess = z.infer<typeof authSuccessSchema>;
export type VerificationRequired = z.infer<
  typeof verificationRequiredSchema
>;
export type AuthResult = z.infer<typeof authResultSchema>;
export type MessageResponse = z.infer<typeof messageResponseSchema>;
